import React, { useState, useRef, useEffect } from 'react';
import { useNotifications } from '../contexts/NotificationContext';

const NotificationBell: React.FC = () => {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const getNotificationIcon = (type: string) => {
    switch (type) {
      case 'success':
        return (
          <svg className="w-5 h-5 text-success" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
          </svg>
        );
      case 'error':
        return (
          <svg className="w-5 h-5 text-danger" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
          </svg>
        );
      case 'warning':
        return (
          <svg className="w-5 h-5 text-warning" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-5 h-5 text-chart" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const formatTimeAgo = (timestamp: Date) => {
    const now = new Date();
    const diffInMinutes = Math.floor((now.getTime() - timestamp.getTime()) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    
    const diffInHours = Math.floor(diffInMinutes / 60);
    if (diffInHours < 24) return `${diffInHours}h ago`;
    
    const diffInDays = Math.floor(diffInHours / 24);
    return `${diffInDays}d ago`;
  };

  const handleNotificationClick = (notification: any) => {
    markAsRead(notification.id);
    if (notification.actionUrl) {
      window.location.href = notification.actionUrl;
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Notification Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-text hover:text-chart transition-colors"
      >
        <svg className="w-7 h-7" fill="currentColor" viewBox="0 0 20 20">
          <path d="M10 2a6 6 0 00-6 6c0 1.887-.454 3.665-1.257 5.234a.75.75 0 00.515 1.076 32.91 32.91 0 003.256.508 3.5 3.5 0 006.972 0 32.91 32.91 0 003.256-.508.75.75 0 00.515-1.076A11.448 11.448 0 0116 8a6 6 0 00-6-6zM8.05 14.943a33.54 33.54 0 003.9.574 2 2 0 01-3.9-.574z" />
        </svg>
        
        {/* Unread Badge */}
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 bg-danger text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Notification Dropdown */}
      {isOpen && (
        <div 
          className="absolute right-0 mt-2 bg-shell border border-line rounded-lg shadow-lg z-50 max-h-96 overflow-y-auto"
          style={{
            width: '400px',
            minWidth: '400px',
            maxWidth: '400px',
            writingMode: 'horizontal-tb',
            textOrientation: 'mixed',
            direction: 'ltr'
          }}
        >
          {/* Header */}
          <div className="p-4 border-b border-line">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold text-text">Notifications</h3>
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="text-sm text-chart hover:text-chart/80 transition-colors"
                >
                  Mark all as read
                </button>
              )}
            </div>
          </div>

          {/* Notifications List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications.length === 0 ? (
              <div className="p-8 text-center">
                <svg className="w-12 h-12 text-muted mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-5 5v-5zM4.828 7l2.586 2.586a2 2 0 002.828 0L12.828 7H4.828zM4 12h16l-1 1H5l-1-1z" />
                </svg>
                <p className="text-muted">No notifications yet</p>
              </div>
            ) : (
              notifications.map((notification) => (
                <div
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={`p-4 border-b border-line cursor-pointer hover:bg-surface/50 transition-colors ${
                    !notification.read ? 'bg-chart/5' : ''
                  }`}
                >
                  <div 
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      alignItems: 'flex-start',
                      gap: '12px',
                      width: '100%',
                      writingMode: 'horizontal-tb',
                      textOrientation: 'mixed'
                    }}
                  >
                    <div style={{flexShrink: 0, marginTop: '4px'}}>
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div 
                      style={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        writingMode: 'horizontal-tb',
                        textOrientation: 'mixed',
                        direction: 'ltr'
                      }}
                    >
                      <div 
                        style={{
                          display: 'flex',
                          flexDirection: 'row',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: '8px',
                          marginBottom: '4px'
                        }}
                      >
                        <h4 
                          className={`text-sm font-medium ${!notification.read ? 'text-text' : 'text-muted'}`}
                          style={{
                            flex: 1,
                            writingMode: 'horizontal-tb',
                            textOrientation: 'mixed',
                            whiteSpace: 'normal',
                            wordWrap: 'break-word',
                            overflowWrap: 'break-word',
                            margin: 0,
                            padding: 0
                          }}
                        >
                          {notification.title}
                        </h4>
                        <span 
                          className="text-xs text-muted"
                          style={{
                            flexShrink: 0,
                            writingMode: 'horizontal-tb',
                            textOrientation: 'mixed',
                            whiteSpace: 'nowrap',
                            margin: 0,
                            padding: 0
                          }}
                        >
                          {formatTimeAgo(notification.timestamp)}
                        </span>
                      </div>
                      <p 
                        className={`text-sm ${!notification.read ? 'text-text' : 'text-muted'}`}
                        style={{
                          writingMode: 'horizontal-tb',
                          textOrientation: 'mixed',
                          whiteSpace: 'normal',
                          wordWrap: 'break-word',
                          overflowWrap: 'break-word',
                          margin: 0,
                          padding: 0,
                          lineHeight: '1.4'
                        }}
                      >
                        {notification.message}
                      </p>
                      {notification.actionText && (
                        <button 
                          className="text-xs text-chart hover:text-chart/80 transition-colors"
                          style={{
                            writingMode: 'horizontal-tb',
                            textOrientation: 'mixed',
                            whiteSpace: 'normal',
                            marginTop: '8px',
                            margin: 0,
                            padding: 0,
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer'
                          }}
                        >
                          {notification.actionText} →
                        </button>
                      )}
                    </div>
                    {!notification.read && (
                      <div
                        style={{
                          width: '8px',
                          height: '8px',
                          backgroundColor: 'rgb(var(--color-chart))',
                          borderRadius: '50%',
                          flexShrink: 0,
                          marginTop: '8px'
                        }}
                      ></div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="p-4 border-t border-line">
              <button
                onClick={() => {
                  setIsOpen(false);
                  window.location.href = '/app/notifications';
                }}
                className="w-full text-center text-sm text-chart hover:text-chart/80 transition-colors"
              >
                View all notifications
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
